/**
 * Files helpers shared by the sidebar, the screen and its dialogs: icons and names for rows, short dates, the
 * "New" menu (with its file and folder pickers) and the row actions menu.
 */
import type { DriveItem, DriveView } from "@bundle/contracts";
import { Drive, Files, Shell, type DriveRow, type DriveViewModel, type DriveViewState } from "@bundle/core";
import type { IconName } from "@bundle/tokens";
import {
  Button,
  FilePicker,
  Icon,
  Menu,
  useDrive,
  useFilePlatform,
  useLocale,
  useNavigation,
  useT,
  useWorkspace,
  type MenuEntry,
  type PickedTreeFile,
} from "@bundle/ui";
import { useState } from "react";

export const VIEW_ICONS: Record<DriveView, IconName> = {
  my: "folder",
  shared: "users",
  recent: "clock",
  starred: "star",
  trash: "trash",
  conversations: "message",
};

/** A row's icon (folders, pages and databases have their own; files by kind). */
export function rowIcon(r: Pick<DriveRow, "kind" | "mime" | "name"> & { item?: DriveItem }): IconName {
  const kind = r.kind === "shortcut" ? r.item?.targetKind ?? "file" : r.kind;
  if (kind === "folder") return "folder";
  if (kind === "page") return "page";
  if (kind === "database") return "database";
  return Files.fileIcon(Files.fileKind(r.mime ?? "", r.name)) as IconName;
}

/** "10:42" today, "Oct 24" this year, "Oct 24, 2025" before. */
export function shortDate(ts: number, locale: string, now = Date.now()): string {
  const d = new Date(ts);
  const n = new Date(now);
  if (d.toDateString() === n.toDateString())
    return new Intl.DateTimeFormat(locale, {
      hour: "numeric",
      minute: "2-digit",
    }).format(d);
  const sameYear = d.getFullYear() === n.getFullYear();
  return new Intl.DateTimeFormat(locale, sameYear ? { month: "short", day: "numeric" } : { month: "short", day: "numeric", year: "numeric" }).format(d);
}

/** The URL of a Files location. */
export function filesUrl(ws: string, r: { view?: DriveView; folderId?: string; itemId?: string; q?: string }): string {
  return Shell.formatUrl({ type: "files", ws, ...r });
}

/** A person's name ("me" for me). */
export function useNameOf(): (userId: string) => string {
  const ws = useWorkspace();
  const t = useT();
  return (userId) => (userId === ws.state.meId ? t("drive.owner.me") : ws.state.cache?.members[userId]?.displayName ?? userId);
}

export function useSize(): (bytes: number) => string {
  const locale = useLocale();
  return (bytes) => Files.formatFileSize(bytes, locale);
}

function toUploads(tree: PickedTreeFile[]) {
  return tree.map((f) => (f.dirs.length === 0 ? { file: f.file } : { file: f.file, dirs: f.dirs }));
}

/**
 * "+ New": New folder, File upload, Folder upload, then Page and Database (a private page placed in the open folder;
 * it opens right away). `variant` "primary" is the sidebar's big button.
 */
export function NewMenu({ variant, testID, open }: { variant: "primary" | "secondary"; testID: string; open: (url: string) => void }) {
  const t = useT();
  const drive = useDrive();
  const ws = useWorkspace();
  const [pick, setPick] = useState<"files" | "folder" | null>(null);
  if (drive === null) return null;
  const { vm, state } = drive;
  const disabled = !state.canAdd && state.location.view === "my";
  const newPage = async (kind: "page" | "database") => {
    const pageId = await vm.createPage(kind, t("drive.new.untitled"));
    if (pageId !== undefined) open(Shell.formatUrl(kind === "page" ? { type: "page", ws: ws.slug, pageId } : { type: "db", ws: ws.slug, dbId: pageId }));
  };
  const items: MenuEntry[] = [
    {
      id: "folder",
      label: t("drive.new.folder"),
      icon: "folder",
      disabled,
      onSelect: () => vm.openNewFolder(),
    },
    { id: "sep1", separator: true },
    {
      id: "file",
      label: t("drive.new.fileUpload"),
      icon: "upload",
      disabled,
      onSelect: () => setPick("files"),
    },
    {
      id: "dir",
      label: t("drive.new.folderUpload"),
      icon: "folder",
      disabled,
      onSelect: () => setPick("folder"),
    },
    { id: "sep2", separator: true },
    {
      id: "page",
      label: t("drive.new.page"),
      icon: "page",
      onSelect: () => void newPage("page"),
    },
    {
      id: "database",
      label: t("drive.new.database"),
      icon: "database",
      onSelect: () => void newPage("database"),
    },
  ];
  return (
    <>
      <Menu items={items} testID={`${testID}-menu`}>
        <Button label={t("drive.new")} icon="plus" variant={variant} size={variant === "primary" ? "md" : "sm"} onPress={() => undefined} testID={testID} />
      </Menu>
      <FilePicker
        open={pick === "files"}
        multiple
        onPick={(files) => vm.upload(files.map((file) => ({ file })))}
        onClose={() => setPick(null)}
        testID={`${testID}-files`}
      />
      <FilePicker
        open={pick === "folder"}
        directory
        onPickTree={(tree) => vm.upload(toUploads(tree))}
        onPick={() => undefined}
        onClose={() => setPick(null)}
        testID={`${testID}-folder`}
      />
    </>
  );
}

/** Opens a row: folder → list, file → preview over the list, page → the page, attachment → its file page. */
export function useOpenRow(): (vm: DriveViewModel, row: DriveRow) => void {
  const ws = useWorkspace();
  const nav = useNavigation();
  return (vm, row) => {
    const d = vm.destination(row);
    switch (d.kind) {
      case "folder":
        nav.open(filesUrl(ws.slug, { folderId: d.folderId }));
        return;
      case "preview":
        nav.open(filesUrl(ws.slug, { itemId: d.itemId }));
        return;
      case "page":
        nav.open(Shell.formatUrl(d.database ? { type: "db", ws: ws.slug, dbId: d.pageId } : { type: "page", ws: ws.slug, pageId: d.pageId }));
        return;
      case "file":
        nav.open(Shell.formatUrl({ type: "file", ws: ws.slug, fileId: d.fileId }));
        return;
    }
  };
}

/** The actions for the selection (toolbar, context menu, "⋮"). In Trash: Restore, Delete forever. */
export function useRowActions(): (
  vm: DriveViewModel,
  state: DriveViewState,
  rows: DriveRow[],
  confirm: (kind: "deleteForever", rows: DriveRow[]) => void
) => MenuEntry[] {
  const t = useT();
  const ws = useWorkspace();
  const platform = useFilePlatform();
  const open = useOpenRow();
  return (vm, state, rows, confirm) => {
    if (rows.length === 0) return [];
    const keys = rows.map((r) => r.key);
    const one = rows.length === 1 ? rows[0]! : undefined;
    const inTrash = state.location.view === "trash";
    if (inTrash) {
      return [
        {
          id: "restore",
          label: t("drive.action.restore"),
          icon: "reload",
          onSelect: () => void vm.restore(keys),
        },
        {
          id: "delete",
          label: t("drive.action.deleteForever"),
          icon: "trash",
          destructive: true,
          disabled: !vm.can(rows, "deleteForever"),
          onSelect: () => confirm("deleteForever", rows),
        },
      ];
    }
    const items = rows.every((r) => r.item !== undefined);
    const out: MenuEntry[] = [];
    if (one !== undefined)
      out.push({
        id: "open",
        label: t("drive.action.open"),
        icon: "externalLink",
        onSelect: () => open(vm, one),
      });
    if (one?.item !== undefined) {
      out.push({
        id: "share",
        label: t("drive.action.share"),
        icon: "users",
        disabled: !vm.can(rows, "view"),
        onSelect: () => vm.openShare(one.key),
      });
      out.push({
        id: "link",
        label: t("drive.action.copyLink"),
        icon: "link",
        onSelect: () => platform.copyLink(filesUrl(ws.slug, { itemId: one.id })),
      });
    }
    if (one?.attachment !== undefined)
      out.push({
        id: "link",
        label: t("drive.action.copyLink"),
        icon: "link",
        onSelect: () => platform.copyLink(Shell.formatUrl({ type: "file", ws: ws.slug, fileId: one.id })),
      });
    out.push({ id: "sep1", separator: true });
    if (one?.item !== undefined)
      out.push({
        id: "rename",
        label: t("drive.action.rename"),
        icon: "edit",
        disabled: !vm.can(rows, "rename"),
        onSelect: () => vm.openRename(one.key),
      });
    if (items) {
      out.push({
        id: "move",
        label: t("drive.action.move"),
        icon: "arrowRight",
        disabled: !vm.can(rows, "move"),
        onSelect: () => vm.openMove(keys),
      });
      if (rows.every((r) => r.kind !== "folder"))
        out.push({
          id: "copy",
          label: t("drive.action.copy"),
          icon: "copy",
          disabled: !vm.can(rows, "copy"),
          onSelect: () => void vm.copy(keys),
        });
      const starred = rows.every((r) => r.starred);
      out.push({
        id: "star",
        label: starred ? t("drive.action.unstar") : t("drive.action.star"),
        icon: starred ? "starOff" : "star",
        onSelect: () => void vm.star(keys, !starred),
      });
    }
    if (one?.item?.kind === "file") {
      out.push({
        id: "versions",
        label: t("drive.action.versions"),
        icon: "clock",
        onSelect: () => {
          vm.showPreview(one.id, one.item);
          vm.toggleVersions(true);
        },
      });
    }
    const files = rows.flatMap((r) => (r.item?.file !== undefined ? [r.item.file] : r.attachment !== undefined ? [r.attachment.file] : []));
    if (files.length === rows.length && files.length > 0)
      out.push({
        id: "download",
        label: t("drive.action.download"),
        icon: "download",
        onSelect: () => files.forEach((f) => platform.download(f)),
      });
    out.push({
      id: "details",
      label: t("drive.action.details"),
      icon: "properties",
      onSelect: () => vm.toggleDetails(true),
    });
    if (items) {
      out.push({ id: "sep2", separator: true });
      out.push({
        id: "trash",
        label: t("drive.action.trash"),
        icon: "trash",
        destructive: true,
        disabled: !vm.can(rows, "trash"),
        onSelect: () => void vm.trash(keys),
      });
    }
    return out;
  };
}

/** The kind's name for the details pane ("Folder", "PDF", "Page"). */
export function useKindName(): (r: DriveRow) => string {
  const t = useT();
  return (r) => {
    if (r.kind === "folder") return t("drive.kind.folder");
    if (r.kind === "page") return t("drive.kind.page");
    if (r.kind === "database") return t("drive.kind.database");
    if (r.kind === "shortcut") return t("drive.kind.shortcut");
    const kind = Files.fileKind(r.mime ?? "", r.name);
    return t(`files.kind.${kind}` as Parameters<typeof t>[0]);
  };
}

export function RowIcon({ row, size = "icon.md" }: { row: DriveRow; size?: "icon.sm" | "icon.md" | "icon.lg" }) {
  const icon = rowIcon(row);
  const color = row.kind === "folder" ? "accent.text" : "text.secondary";
  return <Icon name={icon} size={size} color={color} />;
}

/** Drive's percent label for the meter (one decimal under 10%). */
export function percentUsed(used: number, quota: number): number {
  const p = Drive.storageFraction(used, quota) * 100;
  return p < 10 ? Math.round(p * 10) / 10 : Math.round(p);
}
